/**
 * FooterDrawer Component
 * 
 * Mobile-only component that provides access to footer content via a slide-up drawer.
 * Displays footer links, community info, and privacy settings in a mobile-friendly format.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PrivacySettings } from '../common/PrivacySettings';

interface FooterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * FooterDrawer component
 * Slide-up drawer containing footer content for mobile devices
 * 
 * @param props - Component props
 * @param props.isOpen - Whether the drawer is open
 * @param props.onClose - Callback to close the drawer
 * @returns FooterDrawer component
 */
export function FooterDrawer({ isOpen, onClose }: FooterDrawerProps) {
  const currentYear = new Date().getFullYear();
  const [isPrivacySettingsOpen, setIsPrivacySettingsOpen] = useState(false);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="footer-drawer-backdrop"
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            animation: 'fadeIn 0.2s ease-out',
          }}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <div
        className={`footer-drawer ${isOpen ? 'open' : ''}`}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'var(--color-surface)',
          borderTop: '4px solid var(--color-accent)',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          maxHeight: '80vh',
          overflowY: 'auto',
          zIndex: 1000,
          transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease-out',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
        }}
        role="dialog"
        aria-label="Footer information"
        aria-modal="true"
      >
        {/* Handle bar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '0.75rem',
            cursor: 'pointer',
          }}
          onClick={onClose}
        >
          <div
            style={{
              width: '40px',
              height: '4px',
              background: 'var(--color-accent)',
              borderRadius: '2px',
              opacity: 0.5,
            }}
            aria-hidden="true"
          />
        </div>

        {/* Content */}
        <div style={{ padding: '0 1.5rem 2rem' }}>
          {/* About Section */}
          <section style={{ marginBottom: '2rem' }}>
            <h3
              style={{
                color: 'var(--color-accent)',
                fontSize: 'var(--font-size-base)',
                marginBottom: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              About 8-Bit Composer
            </h3>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                lineHeight: '1.6',
                opacity: 0.9,
              }}
            >
              Create retro chiptune music in your browser. Free online music maker with 8-bit
              synthesizers, drum machines, and sequencer for composing nostalgic video game
              soundtracks.
            </p>
          </section>

          {/* Quick Links */}
          <section style={{ marginBottom: '2rem' }}>
            <h3
              style={{
                color: 'var(--color-accent)',
                fontSize: 'var(--font-size-base)',
                marginBottom: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              Quick Links
            </h3>
            <nav
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}
            >
              <Link
                to="/"
                onClick={onClose}
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-sm)',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'background 0.2s',
                }}
              >
                🎵 Composer
              </Link>
              <Link
                to="/about"
                onClick={onClose}
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-sm)',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'background 0.2s',
                }}
              >
                ℹ️ About
              </Link>
              <Link
                to="/contact"
                onClick={onClose}
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-sm)',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'background 0.2s',
                }}
              >
                📧 Contact
              </Link>
              <Link
                to="/faq"
                onClick={onClose}
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-sm)',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'background 0.2s',
                }}
              >
                ❓ FAQ
              </Link>
              <Link
                to="/terms"
                onClick={onClose}
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-sm)',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'background 0.2s',
                }}
              >
                📜 Terms & Ownership
              </Link>
            </nav>
          </section>

          {/* Community */}
          <section style={{ marginBottom: '2rem' }}>
            <h3
              style={{
                color: 'var(--color-accent)',
                fontSize: 'var(--font-size-base)',
                marginBottom: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              Community
            </h3>
            <a
              href="https://discord.gg/NDHSpd7be5"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--color-fg)',
                textDecoration: 'none',
                fontSize: 'var(--font-size-sm)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem',
                borderRadius: '4px',
                transition: 'background 0.2s',
              }}
            >
              💬 Discord Server
            </a>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                lineHeight: '1.5',
                opacity: 0.8,
                marginTop: '0.5rem',
                paddingLeft: '0.5rem',
              }}
            >
              Join our community to share compositions, get help, and connect with other creators!
            </p>
          </section>

          {/* Legal & Privacy */}
          <section style={{ marginBottom: '2rem' }}>
            <h3
              style={{
                color: 'var(--color-accent)',
                fontSize: 'var(--font-size-base)',
                marginBottom: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              Legal & Privacy
            </h3>
            <button
              onClick={() => setIsPrivacySettingsOpen(true)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-fg)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family)',
                padding: '0.5rem',
                textAlign: 'left',
                width: '100%',
                borderRadius: '4px',
                transition: 'background 0.2s',
              }}
              aria-label="Privacy settings"
            >
              🔒 Privacy Settings
            </button>
            <Link
              to="/privacy"
              onClick={onClose}
              style={{
                color: 'var(--color-fg)',
                textDecoration: 'none',
                fontSize: 'var(--font-size-sm)',
                display: 'block',
                padding: '0.5rem',
                borderRadius: '4px',
                transition: 'background 0.2s',
              }}
            >
              📄 Privacy Policy
            </Link>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                lineHeight: '1.5',
                opacity: 0.8,
                marginTop: '0.5rem',
                paddingLeft: '0.5rem',
              }}
            >
              Your privacy matters. Control your data and analytics preferences.
            </p>
          </section>

          {/* Divider */}
          <div
            style={{
              borderTop: '1px solid var(--color-accent)',
              opacity: 0.3,
              marginBottom: '1.5rem',
            }}
          />

          {/* Bottom Info */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: 'var(--font-size-xs)',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: 0, opacity: 0.9 }}>Powered by pixels and patterns ✨</p>
            <p style={{ margin: 0 }}>
              Crafted with love by{' '}
              <a
                href="https://doriansmith.dev"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--color-accent)',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                }}
              >
                Dorian Smith
              </a>
            </p>
            <p style={{ margin: 0, opacity: 0.8 }}>
              {currentYear} © All bleeps and bloops reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Privacy Settings Modal */}
      <PrivacySettings
        isOpen={isPrivacySettingsOpen}
        onClose={() => setIsPrivacySettingsOpen(false)}
      />
    </>
  );
}
